import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getApplicationData, updateApplicationCurrentFlow } from 'thunks';
import { ApplicationStatusName } from 'enums/ApplicationStatusName';
import { RoutePath } from 'enums/Routes';
import { ApplicationStepName } from 'enums/ApplicationStepName';
import { getEnvironment, Environments } from 'utils/getEnvironment';
import { CurrentFlow } from 'enums/CurrentFlow';

import { GetApplicationData } from './applicationData';

export const VERIFICATION_PENDING_STATUSES = Object.freeze([
  ApplicationStatusName.ManualVerify,
  ApplicationStatusName.FinalReview,
]);

type StatusPathMap = Partial<Record<ApplicationStatusName, RoutePath>>;

const SHARED_STATUS_PATHS: StatusPathMap = {
  [ApplicationStatusName.Verification]: RoutePath.SetupAccount,
  [ApplicationStatusName.SetUpDeduction]: RoutePath.RepaymentMethod,
  [ApplicationStatusName.SigningAgreement]: RoutePath.DocuSignSignature,
  [ApplicationStatusName.AwaitingFunding]: RoutePath.FundsSent,
  [ApplicationStatusName.Funded]: RoutePath.FundsSent,
};

const DC_STATUS_PATHS: StatusPathMap = {
  ...SHARED_STATUS_PATHS,
  [ApplicationStatusName.OfferAvailable]: RoutePath.OfferAvailable,
  [ApplicationStatusName.ManualVerify]: RoutePath.OfferStatus,
  [ApplicationStatusName.FinalReview]: RoutePath.OfferStatus,
  [ApplicationStatusName.OutOfState]: RoutePath.OutOfState,
  [ApplicationStatusName.AlreadyApplied]: RoutePath.OfferStatus,
  [ApplicationStatusName.Rejected]: RoutePath.OfferStatus,
  [ApplicationStatusName.FinalizeLoan]: RoutePath.SelectDebts,
};

const FC_STATUS_PATHS: StatusPathMap = {
  ...SHARED_STATUS_PATHS,
  [ApplicationStatusName.ManualReview]: RoutePath.FinancialDashboard,
  [ApplicationStatusName.OfferAvailable]: RoutePath.FinancialDashboard,
  [ApplicationStatusName.ManualVerify]: RoutePath.FinancialDashboard,
  [ApplicationStatusName.FinalReview]: RoutePath.FinancialDashboard,
  [ApplicationStatusName.OutOfState]: RoutePath.FinancialDashboard,
  [ApplicationStatusName.AlreadyApplied]: RoutePath.FinancialDashboard,
  [ApplicationStatusName.Rejected]: RoutePath.FinancialDashboard,
  [ApplicationStatusName.FinalizeLoan]: RoutePath.FinancialDashboard,
};

const AI_COACH_STATUS_PATHS: StatusPathMap = {
  ...DC_STATUS_PATHS,
  [ApplicationStatusName.ManualReview]: RoutePath.FinancialDashboard,
};

const getPathByStatus = (status: ApplicationStatusName, map: StatusPathMap): RoutePath => map[status] as RoutePath;

export const FLOW_TO_STEP_MAP: Record<
  CurrentFlow,
  (application?: GetApplicationData, completedCustomize?: boolean) => { path: RoutePath }
> = {
  [CurrentFlow.DebtConsolidation]: (application?: GetApplicationData) => {
    if (!application?.status) return { path: RoutePath.Goals };
    if (application.status === ApplicationStatusName.ManualReview) {
      return { path: application.frozenCreditScore ? RoutePath.UnfreezeCreditScore : RoutePath.OfferStatus };
    }
    return { path: getPathByStatus(application.status, DC_STATUS_PATHS) };
  },
  [CurrentFlow.FinancialCheckup]: (application?: GetApplicationData) => ({
    path: application?.status ? getPathByStatus(application.status, FC_STATUS_PATHS) : RoutePath.Goals,
  }),
  [CurrentFlow.Card]: (application?: GetApplicationData, completedCustomize?: boolean) => {
    if (application?.status) return { path: RoutePath.CardApplicationSuccessful };
    return { path: completedCustomize ? RoutePath.YourPhoneNumber : RoutePath.YourName };
  },
  [CurrentFlow.StudentLoanForgiveness]: (application?: GetApplicationData) =>
    application?.status ? { path: RoutePath.StudentLoanEmployerType } : { path: RoutePath.Goals },
  [CurrentFlow.MissedPayment]: () => ({ path: RoutePath.MissedPayment }),
  [CurrentFlow.AICoach]: (application?: GetApplicationData) => {
    if (!application?.status) return { path: RoutePath.AICoachGoals };
    if ([ApplicationStatusName.Rejected, ApplicationStatusName.OfferAvailable].includes(application.status)) {
      return { path: RoutePath.FinancialDashboard };
    }
    if (application.status === ApplicationStatusName.ManualReview && application.frozenCreditScore) {
      return { path: RoutePath.UnfreezeCreditScore };
    }
    return { path: getPathByStatus(application.status, AI_COACH_STATUS_PATHS) };
  },
};

interface ApplicationStep {
  stepName?: RoutePath;
  stepParams?: Record<string, string>;
  currentFlow: CurrentFlow;
  completedCustomize?: boolean;
}

const initialState: ApplicationStep = { currentFlow: CurrentFlow.AICoach };

const updateAnalytics = (state: ApplicationStep) => {
  const step = ApplicationStepName[state.stepName as RoutePath];

  if (!step && getEnvironment() === Environments.Staging) {
    return;
  }

  analytics.track(`${step} Step`);
  (window as any).nid('start', step);
};

const applicationStep = createSlice({
  name: 'applicationStep',
  initialState,
  reducers: {
    setApplicationStep: (
      state: ApplicationStep,
      { payload }: PayloadAction<{ stepName: RoutePath; stepParams?: Record<string, string> }>,
    ) => {
      state.stepName = payload.stepName;
      state.stepParams = payload.stepParams;
      updateAnalytics(state);
    },
    setApplicationFlow: (state: ApplicationStep, { payload }: PayloadAction<ApplicationStep>) => {
      state.currentFlow = payload.currentFlow ?? CurrentFlow.AICoach;
      state.completedCustomize = payload.completedCustomize;
      state.stepParams = undefined;
      analytics.identify({ currentFlow: payload.currentFlow });
      state.stepName =
        payload.stepName ?? FLOW_TO_STEP_MAP[state.currentFlow!](undefined, payload.completedCustomize).path;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getApplicationData.fulfilled, (state, { payload }) => {
      state.currentFlow = payload.application.currentFlow ?? CurrentFlow.AICoach;
      state.stepName = FLOW_TO_STEP_MAP[state.currentFlow](payload.application).path;
      state.stepParams = undefined;
    });

    builder.addCase(updateApplicationCurrentFlow.fulfilled, (state, { payload }) => {
      state.currentFlow = payload;
    });
  },
});

export const { setApplicationStep, setApplicationFlow } = applicationStep.actions;
export default applicationStep.reducer;
